import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`An embedding is a single call to an embedding model to convert text into a vector representation.`}</p>
    <p><strong parentName="p">{`Event name`}</strong>{`: `}<inlineCode parentName="p">{`$ai_embedding`}</inlineCode></p>
    <h3 {...{
      "id": "core-properties"
    }}>{`Core properties`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}><strong parentName="th">{`Property`}</strong></th>
          <th parentName="tr" {...{
            "align": null
          }}><strong parentName="th">{`Description`}</strong></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_trace_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The trace ID (a UUID to group related AI events together). Must contain only letters, numbers, and special characters: `}<inlineCode parentName="td">{`-`}</inlineCode>{`, `}<inlineCode parentName="td">{`_`}</inlineCode>{`, `}<inlineCode parentName="td">{`~`}</inlineCode>{`, `}<inlineCode parentName="td">{`.`}</inlineCode>{`, `}<inlineCode parentName="td">{`@`}</inlineCode>{`, `}<inlineCode parentName="td">{`(`}</inlineCode>{`, `}<inlineCode parentName="td">{`)`}</inlineCode>{`, `}<inlineCode parentName="td">{`!`}</inlineCode>{`, `}<inlineCode parentName="td">{`'`}</inlineCode>{`, `}<inlineCode parentName="td">{`:`}</inlineCode>{`, `}<code>{`|`}</code>{` `}<br />{`Example: `}<inlineCode parentName="td">{`d9222e05-8708-41b8-98ea-d4a21849e761`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_span_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Unique identifier for this embedding operation`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_span_name`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Name given to this embedding operation `}<br />{`Example: `}<inlineCode parentName="td">{`embed_user_query`}</inlineCode>{`, `}<inlineCode parentName="td">{`index_document`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_parent_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Parent span ID for tree-view grouping`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_model`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The embedding model used`}<br />{`Example: `}<inlineCode parentName="td">{`text-embedding-3-small`}</inlineCode>{`, `}<inlineCode parentName="td">{`text-embedding-ada-002`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_provider`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The LLM provider`}<br />{`Example: `}<inlineCode parentName="td">{`openai`}</inlineCode>{`, `}<inlineCode parentName="td">{`cohere`}</inlineCode>{`, `}<inlineCode parentName="td">{`voyage`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_input`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The text to embed`}<br />{`Example: `}<inlineCode parentName="td">{`"Tell me a fun fact about hedgehogs"`}</inlineCode>{` or array of strings for batch embeddings`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_input_tokens`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The number of tokens in the input`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_latency`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The latency of the embedding call in seconds`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_http_status`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The HTTP status code of the response`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_base_url`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The base URL of the LLM provider`}<br />{`Example: `}<inlineCode parentName="td">{`https://api.openai.com/v1`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_request_url`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The full URL of the request made to the embedding API`}<br />{`Example: `}<inlineCode parentName="td">{`https://api.openai.com/v1/embeddings`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_is_error`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Boolean to indicate if the request was an error`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_error`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` The error message or object if the embedding failed`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "cost-properties"
    }}>{`Cost properties`}</h3>
    <p>{`Cost properties are optional as we can automatically calculate them from model and token counts. If you want, you can provide your own cost property instead.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}><strong parentName="th">{`Property`}</strong></th>
          <th parentName="tr" {...{
            "align": null
          }}><strong parentName="th">{`Description`}</strong></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_input_cost_usd`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Cost in USD for input tokens`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_output_cost_usd`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{`  Cost in USD for output tokens (usually 0 for embeddings)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ai_total_cost_usd`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`(Optional)`}</em>{` Total cost in USD`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "example-api-call"
    }}>{`Example API call`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl -X POST "<ph_client_api_host>/i/v0/e/" \\
     -H "Content-Type: application/json" \\
     -d '{
         "api_key": "<ph_project_api_key>",
         "event": "$ai_embedding",
         "properties": {
             "distinct_id": "user_123",
             "$ai_trace_id": "d9222e05-8708-41b8-98ea-d4a21849e761",
             "$ai_model": "text-embedding-3-small",
             "$ai_provider": "openai",
             "$ai_input": "What are the key features of product analytics at PostHog?",
             "$ai_input_tokens": 12,
             "$ai_latency": 0.234,
             "$ai_http_status": 200,
             "$ai_base_url": "https://api.openai.com/v1",
             "$ai_request_url": "https://api.openai.com/v1/embeddings",
             "$ai_is_error": false,
             "$ai_span_name": "embed_search_query",
             "$ai_span_id": "bdf42359-9364-4db7-8958-c001f28c9255",
             "$ai_parent_id": "537b7988-0186-494f-a313-77a5a8f7db26"
         },
         "timestamp": "2025-01-30T12:00:00Z"
     }'
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      